import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authApi } from '../lib/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [resetInfo, setResetInfo] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResetInfo(null);

    try {
      const response = await authApi.forgotPassword(email);
      setSuccess(true);
      
      // If email wasn't sent (no SendGrid), show the reset link
      if (response.data.reset_link) {
        setResetInfo({
          link: response.data.reset_link,
          token: response.data.reset_token
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-[#1A1A2E] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-[#26C6DA] flex items-center justify-center">
              <span className="text-white font-bold text-xl" style={{ fontFamily: 'Outfit' }}>SA</span>
            </div>
            <h1 className="text-2xl font-bold text-[#26C6DA]" style={{ fontFamily: 'Outfit' }}>
              Strategic Architect
            </h1>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-slate-200 dark:border-[#1F2937]">
          {!success ? (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#26C6DA]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-[#26C6DA]" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                  Forgot Password?
                </h2>
                <p className="text-slate-500 dark:text-slate-400 mt-2">
                  Enter your email and we'll send you a reset link
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="bg-slate-100 dark:bg-[#1A1A2E]/50 border-slate-200 dark:border-[#1F2937] focus:border-[#0F5156] focus:ring-[#0F5156]"
                    data-testid="forgot-email-input"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full rounded-full bg-[#26C6DA] hover:bg-[#26C6DA]/90 py-6"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Outfit' }}>
                Check Your Email
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                If an account exists for <strong>{email}</strong>, you'll receive a password reset link shortly.
              </p>

              {resetInfo && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
                  <p className="text-amber-800 text-sm font-medium mb-2">
                    📧 Email service not configured. Use this link to reset your password:
                  </p>
                  <Link 
                    to={`/reset-password?token=${resetInfo.token}`}
                    className="text-[#26C6DA] underline text-sm break-all"
                  >
                    {resetInfo.link}
                  </Link>
                </div>
              )}

              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => {
                  setSuccess(false);
                  setEmail('');
                  setResetInfo(null);
                }}
              >
                Send Another Link
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center gap-2 text-sm text-[#26C6DA] hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
